/* eslint-disable */
import React, { useState, useEffect } from "react";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { useTranslation } from "react-i18next";

import { getData, postData, putData } from "utils/Gateway";
import * as url from "helper/UrlHelper";
import { reactSelectStyle } from "Config/Config";

const PROVIDER_OPTIONS = [
  { label: "Retell", value: "retell" },
  { label: "PBX", value: "pbx" },
];

// Sample payload shown to the user so they know what JSON to expect
const DEMO_PAYLOAD = {
  retell: {
    event: "call_ended",
    call: {
      call_id: "call_abc123xyz",
      call_type: "inbound",
      from_number: "+14155552671",
      to_number: "+14155550100",
      direction: "inbound",
      start_timestamp: 1712000000000,
      end_timestamp: 1712000180000,
      duration_ms: 180000,
      call_status: "ended",
      disconnection_reason: "caller_hangup",
      transcript: "Hello, I'd like to inquire about your services...",
      recording_url: "https://storage.retellai.com/recordings/call_abc123xyz.mp3",
      agent_id: "agent_abc123",
      metadata: {}
    }
  },
  pbx: {
    provider: "asterisk",
    event: "call_end",
    uniqueid: "1712000000.1",
    linkedid: "1712000000.1",
    callerid_num: "14155552671",
    callerid_name: "John Doe",
    dst: "1001",
    direction: "inbound",
    start: "2024-04-01 12:00:00",
    end: "2024-04-01 12:03:00",
    duration: 180,
    billsec: 175,
    disposition: "ANSWERED",
    recording: "https://your-pbx-server.com/recordings/20240401-120000-1712000000.wav",
    transcript: ""
  }
};

const SaveWebhookOffcanvas = ({
  editWebhookId,
  afterSave = () => {},
  setShowAlert = () => {},
  setAlertMessage = () => {},
  setMessageType = () => {},
}) => {
  const { t } = useTranslation();
  const userInfo = JSON.parse(localStorage.getItem("userInfo"));

  const isAdmin =
    userInfo?.role?.slug === "ADMIN" || userInfo?.role?.slug === "SUPER_ADMIN";

  const serverBase = process.env.REACT_APP_SERVER_URL || "https://api.lobees.com";

  // form state
  const [name, setName] = useState("");
  const [providerValue, setProviderValue] = useState(null);
  const [generatedCustomId, setGeneratedCustomId] = useState("");
  const [active, setActive] = useState(true);
  const [autoCreateLead, setAutoCreateLead] = useState(false);
  const [attachRecording, setAttachRecording] = useState(false);

  const [copiedUrl, setCopiedUrl] = useState(false);

  // company select
  const [companyInput, setCompanyInput] = useState("");
  const [companyList, setCompanyList] = useState([]);
  const [companyValue, setCompanyValue] = useState(null);
  const [companyId, setCompanyId] = useState(null);

  // user select (admin only)
  const [userInput, setUserInput] = useState("");
  const [userOptions, setUserOptions] = useState([]);
  const [userValue, setUserValue] = useState(null);
  const [userId, setUserId] = useState(null);

  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const fullWebhookUrl =
    generatedCustomId && providerValue
      ? `${serverBase}/api/webhook/${providerValue.value}/${generatedCustomId}`
      : "";

  const generateCustomId = (provider) => {
    if (!editWebhookId && provider) {
      const rand = Math.random().toString(36).slice(2, 12);
      setGeneratedCustomId(`wh_${rand}`);
    }
  };

  const copyUrlToClipboard = () => {
    if (!fullWebhookUrl) return;
    navigator.clipboard.writeText(fullWebhookUrl).then(() => {
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    });
  };

  const getCompanyList = async () => {
    try {
      const requestUrl =
        `${url.API_BASE_URL}${url.API_GET_ALL_COMPANY_BY_SEARCH}` +
        `?filtername=${companyInput}`;
      const response = await getData(requestUrl);
      if (response.status) {
        setCompanyList(
          response.data.map((item) => ({
            label: item.name,
            value: item._id,
            _id: item._id,
          }))
        );
      }
    } catch (err) {
      console.log(err.message);
    }
  };

  const getUserList = async () => {
    try {
      const requestUrl =
        url.API_BASE_URL +
        url.API_GET_USERLIST_OPTIONS +
        `&userstringinput=${userInput}`;
      const response = await getData(requestUrl);
      if (response.status && response.data.length > 0) {
        setUserOptions(response.data);
      }
    } catch (err) {
      console.log(err.message);
    }
  };

  const loadWebhookDetails = async () => {
    try {
      const requestUrl =
        url.API_BASE_URL + url.API_GET_WEBHOOK_DETAILS + `/${editWebhookId}`;
      const response = await getData(requestUrl);
      if (response.status) {
        const d = response.data;
        setName(d.name || "");
        setGeneratedCustomId(d.customid || "");
        setActive(d.active !== undefined ? d.active : true);
        setAutoCreateLead(d.settings?.autoCreateLead || false);
        setAttachRecording(d.settings?.attachRecording || false);

        if (d.provider) {
          setProviderValue(PROVIDER_OPTIONS.find((p) => p.value === d.provider) || null);
        }
        if (d.company) {
          setCompanyValue({ label: d.company.name, value: d.company._id, _id: d.company._id });
          setCompanyId(d.company._id);
        }
        if (d.user) {
          setUserValue({
            label: `${d.user.name || ""} ${d.user.surname || ""}`.trim() || d.user.email,
            value: d.user._id,
            _id: d.user._id,
            email: d.user.email,
          });
          setUserId(d.user._id);
        }
      }
    } catch (err) {
      console.log(err.message);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!name.trim()) newErrors.name = t("Please enter a webhook name");
    if (!providerValue) newErrors.provider = t("Please select a provider");
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveHandler = async () => {
    if (!validateForm()) return;
    try {
      setIsSaving(true);
      const payload = {
        name,
        provider: providerValue.value,
        company: companyId || null,
        user: userId || null,
        customid: editWebhookId ? undefined : generatedCustomId,
        active,
        settings: { autoCreateLead, attachRecording },
      };

      let response;
      if (editWebhookId) {
        response = await putData(
          url.API_BASE_URL + url.API_UPDATE_WEBHOOK + `/${editWebhookId}`,
          payload
        );
      } else {
        response = await postData(url.API_BASE_URL + url.API_CREATE_WEBHOOK, payload);
      }

      setIsSaving(false);
      if (response.status) {
        resetHandler();
        const el = document.querySelector("#saveWebhookOffcanvas");
        if (el) {
          const oc = window.bootstrap.Offcanvas.getInstance(el);
          if (oc) oc.hide();
        }
        afterSave();
        setMessageType("success");
      } else {
        setMessageType("error");
      }
      setAlertMessage(response.message);
      setShowAlert(true);
    } catch (err) {
      setIsSaving(false);
      setAlertMessage(err.message);
      setMessageType("error");
      setShowAlert(true);
    }
  };

  const resetHandler = () => {
    setName("");
    setProviderValue(null);
    setGeneratedCustomId("");
    setActive(true);
    setAutoCreateLead(false);
    setAttachRecording(false);
    setCompanyValue(null);
    setCompanyId(null);
    setCompanyInput("");
    setCompanyList([]);
    setUserValue(null);
    setUserId(null);
    setUserInput("");
    setUserOptions([]);
    setErrors({});
    setCopiedUrl(false);
  };

  useEffect(() => {
    if (companyInput.length > 1) getCompanyList();
  }, [companyInput]);

  useEffect(() => {
    if (isAdmin && userInput.length > 2) getUserList();
  }, [userInput]);

  useEffect(() => {
    if (editWebhookId) {
      loadWebhookDetails();
    } else {
      resetHandler();
    }
  }, [editWebhookId]);

  const demoPayload = providerValue ? DEMO_PAYLOAD[providerValue.value] : null;

  return (
    <div
      className="offcanvas lg offcanvas-end bg-white border-0 shadow-lg"
      tabIndex="-1"
      id="saveWebhookOffcanvas"
      aria-labelledby="saveWebhookOffcanvasLabel"
    >
      {/* ── header ── */}
      <div className="offcanvas-header bg-primary text-white p-4 pb-3">
        <div className="d-flex align-items-center gap-3">
          <div className="p-2 bg-white rounded-circle d-flex align-items-center justify-content-center">
            <span className="material-symbols-outlined text-primary icon-md">webhook</span>
          </div>
          <div>
            <h5 className="offcanvas-title fw-bold text-white mb-0" id="saveWebhookOffcanvasLabel">
              {editWebhookId ? t("Edit Webhook") : t("Add New Webhook")}
            </h5>
            <p className="text-white opacity-75 fs-xs mb-0">
              {t("Configure your webhook settings")}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="btn-close btn-close-white"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
          onClick={resetHandler}
        />
      </div>

      {/* ── body ── */}
      <div className="offcanvas-body px-4 py-3 d-flex flex-column gap-4" style={{ overflowY: "auto" }}>

        {/* ── SECTION: Configuration ── */}
        <div>
          <p className="fs-xs fw-bold text-uppercase text-muted letter-spacing-1 mb-3">
            {t("Configuration")}
          </p>

          <div className="d-flex flex-column gap-3">
            {/* name */}
            <div className="form-group">
              <label className="d-block fs-sm fw-semibold mb-1">
                {t("Webhook Name")} <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control fs-sm shadow-none"
                placeholder={t("e.g. Retell Inbound Calls")}
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (errors.name) setErrors((prev) => ({ ...prev, name: "" }));
                }}
              />
              {errors.name && (
                <div className="invalid-feedback d-block">{errors.name}</div>
              )}
            </div>

            {/* provider */}
            <div className="form-group">
              <label className="d-block fs-sm fw-semibold mb-1">
                {t("Provider")} <span className="text-danger">*</span>
              </label>
              <Select
                isClearable
                placeholder={t("Select provider")}
                styles={reactSelectStyle("3px", "6px", true, false)}
                options={PROVIDER_OPTIONS}
                value={providerValue}
                onChange={(val) => {
                  setProviderValue(val);
                  generateCustomId(val?.value);
                  if (errors.provider) setErrors((prev) => ({ ...prev, provider: "" }));
                }}
              />
              {errors.provider && (
                <div className="invalid-feedback d-block">{errors.provider}</div>
              )}
            </div>

            {/* company (optional) */}
            <div className="form-group">
              <label className="d-block fs-sm fw-semibold mb-1">
                {t("Company")}
                <span className="text-muted fw-normal fs-xs ms-1">({t("optional")})</span>
              </label>
              <Select
                isClearable
                placeholder={t("Type to search company")}
                styles={reactSelectStyle("3px", "6px", true, false)}
                options={companyList}
                value={companyValue}
                onChange={(val) => {
                  setCompanyValue(val);
                  setCompanyId(val?._id || val?.value || null);
                }}
                onInputChange={(val) => setCompanyInput(val)}
              />
            </div>

            {/* user (admin only) */}
            {isAdmin && (
              <div className="form-group">
                <label className="d-block fs-sm fw-semibold mb-1">{t("Assign User")}</label>
                <CreatableSelect
                  isClearable
                  placeholder={t("Search by name or email")}
                  styles={reactSelectStyle("3px", "6px", true, false)}
                  options={userOptions}
                  value={userValue}
                  onChange={(val) => {
                    setUserValue(val);
                    setUserId(val?._id || null);
                  }}
                  onInputChange={(val) => setUserInput(val)}
                />
              </div>
            )}

            {/* settings */}
            <div className="form-group">
              <label className="d-block fs-sm fw-semibold mb-2">{t("Settings")}</label>
              <div className="d-flex flex-column gap-2">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="autoCreateLead"
                    checked={autoCreateLead}
                    onChange={(e) => setAutoCreateLead(e.target.checked)}
                  />
                  <label className="ms-2 form-check-label fs-sm" htmlFor="autoCreateLead">
                    {t("Auto create lead")}
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="attachRecording"
                    checked={attachRecording}
                    onChange={(e) => setAttachRecording(e.target.checked)}
                  />
                  <label className="ms-2 form-check-label fs-sm" htmlFor="attachRecording">
                    {t("Attach recording")}
                  </label>
                </div>
              </div>
            </div>

            {/* active toggle */}
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="webhookActive"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
              />
              <label className="ms-2 form-check-label fs-sm fw-semibold" htmlFor="webhookActive">
                {active ? t("Active") : t("Inactive")}
              </label>
            </div>
          </div>
        </div>

        {/* ── SECTION: What happens when triggered ── */}
        <div className="border rounded-3 p-3 bg-light">
          <div className="d-flex align-items-start gap-2 mb-2">
            <span className="material-symbols-outlined text-primary icon-sm mt-1">info</span>
            <p className="fs-sm fw-semibold mb-0 text-dark">{t("What happens when this webhook is triggered?")}</p>
          </div>
          <ul className="mb-0 ps-4 d-flex flex-column gap-1" style={{ listStyle: "disc" }}>
            <li className="fs-xs text-secondary">
              {t("A new")} <strong>{t("Lead")}</strong> {t("will be created automatically.")}
            </li>
            <li className="fs-xs text-secondary">
              <strong>{t("Owner")}</strong>{" "}
              {userId
                ? <>= <span className="text-primary">{userValue?.label}</span></>
                : t("= the account that owns this webhook")}
            </li>
            <li className="fs-xs text-secondary">
              <strong>{t("Company")}</strong>{" "}
              {companyId
                ? <>= <span className="text-primary">{companyValue?.label}</span></>
                : t("= not linked (optional)")}
            </li>
            {attachRecording && (
              <li className="fs-xs text-secondary">
                {t("The call recording URL will be attached to the lead.")}
              </li>
            )}
          </ul>
        </div>

        {/* ── SECTION: Webhook URL ── */}
        {fullWebhookUrl && (
          <div className="rounded-3 p-3 border border-primary"
          >
            <div className="d-flex align-items-center gap-2 mb-2">
              <span className="material-symbols-outlined text-primary" style={{ fontSize: "20px" }}>
                link
              </span>
              <p className="fw-bold fs-sm text-primary mb-0">
                {t("Your Webhook URL")}
              </p>
              <span className="badge bg-primary ms-auto" style={{ fontSize: "10px" }}>
                {t("Copy & share this")}
              </span>
            </div>

            <div className="d-flex align-items-stretch rounded-2 overflow-hidden border border-primary">
              <div
                className="flex-grow-1 px-3 py-2 d-flex align-items-center"
                style={{ background: "#fff", minWidth: 0 }}
              >
                <span
                  className="text-truncate fs-xs fw-semibold text-dark"
                  style={{ fontFamily: "monospace", letterSpacing: "0.02em" }}
                  title={fullWebhookUrl}
                >
                  {fullWebhookUrl}
                </span>
              </div>
              <button
                type="button"
                onClick={copyUrlToClipboard}
                className={`btn d-flex align-items-center gap-2 px-3 fw-semibold fs-xs rounded-0 ${copiedUrl ? "btn-success" : "btn-primary"}`}
                style={{ whiteSpace: "nowrap", minWidth: "90px", transition: "background 0.2s" }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: "16px" }}>
                  {copiedUrl ? "check_circle" : "content_copy"}
                </span>
                {copiedUrl ? t("Copied!") : t("Copy URL")}
              </button>
            </div>

            <p className="fs-xs text-primary mb-0 mt-2">
              {t("Paste this URL in your")} <strong>{providerValue?.label}</strong> {t("provider settings to start receiving call events.")}
            </p>
          </div>
        )}

        {/* ── SECTION: Expected payload ── */}
        {demoPayload && (
          <div>
            <p className="fs-xs fw-bold text-uppercase text-muted letter-spacing-1 mb-2">
              {t("Expected payload when triggered")}
              <span className="badge bg-secondary ms-2 fw-normal text-capitalize">
                {providerValue.label}
              </span>
            </p>
            <div
              className="rounded-3 border overflow-auto"
              style={{ background: "#1e1e1e", maxHeight: "280px" }}
            >
              <pre className="m-0 p-3 fs-xs" style={{ color: "#d4d4d4", whiteSpace: "pre", fontFamily: "monospace" }}>
                {JSON.stringify(demoPayload, null, 2)}
              </pre>
            </div>
            <p className="fs-xs text-muted mt-1">
              {t("This is a sample of the JSON body your provider will send to the webhook URL. Actual values will vary per call.")}
            </p>
          </div>
        )}
      </div>

      {/* ── footer ── */}
      <div className="offcanvas-footer border-top px-4 py-3 d-flex gap-2 justify-content-end bg-white">
        <button
          type="button"
          className="btn btn-outline-secondary"
          data-bs-dismiss="offcanvas"
          onClick={resetHandler}
        >
          {t("Cancel")}
        </button>
        <button
          type="button"
          className="btn btn-primary d-flex align-items-center gap-2"
          onClick={saveHandler}
          disabled={isSaving}
        >
          {t("Save Webhook")}
          {isSaving && (
            <div className="spinner-border spinner-border-sm" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          )}
        </button>
      </div>
    </div>
  );
};

export default SaveWebhookOffcanvas;
